import ky, { type KyInstance } from 'ky'
import { useAuthStore } from '@/shared/stores/authStore'
import type { ApiErrorResponse } from '../types/session.types'

/**
 * War Room API client.
 * Base URL: /api/v1/war-room (proxied to :8090 by Vite)
 * Matches existing app pattern from TDD-02 §1.
 */
export const warRoomClient: KyInstance = ky.create({
  prefixUrl: '/api/v1/war-room',
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  retry: {
    limit: 2,
    statusCodes: [500, 502, 503],
    methods: ['get'],
  },
  hooks: {
    beforeRequest: [
      (request) => {
        const state = useAuthStore.getState() as any
        const token = state.token || state.accessToken
        if (token) {
          request.headers.set('Authorization', `Bearer ${token}`)
        }
        request.headers.set('X-Request-ID', crypto.randomUUID())
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        if (response.status === 401) {
          useAuthStore.getState().logout()
          window.location.href = '/auth/login'
        }
        return response
      },
    ],
    beforeError: [
      async (error) => {
        const { response } = error
        if (response) {
          try {
            const body = (await response.clone().json()) as ApiErrorResponse
            ;(error as any).code = body?.error?.code
            ;(error as any).warMessage = body?.error?.message
            ;(error as any).traceId = body?.error?.trace_id
          } catch {
            // Response not JSON — keep original error
          }
        }
        return error
      },
    ],
  },
})

/**
 * Helper: extract typed error from ky HTTPError
 */
export function getWarRoomErrorCode(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err && typeof (err as any).code === 'string') {
    return (err as any).code
  }
  return 'internal.error'
}
